<template>
    <v-container fluid class="pa-0">
        <template v-if="loadingProfile">
            <v-overlay :value="loadingProfile">
                <v-progress-circular
                    :size="100"
                    color="white"
                    indeterminate
                ></v-progress-circular>
            </v-overlay>
        </template>
        <template v-else>
            <tutor-hero 
                :id="tutor.userData.id"
                :firstName="tutor.userData.firstName"
                :lastName="tutor.userData.lastName"
                :bio="tutor.userData.bio"
                :education="tutor.userData.education"
                :teachingExperience="tutor.userData.teachingExperience"
                :photo="profilePicture"
                :subjects="tutor.teachingSubjects"
                :country="tutor.country"
                :review="tutor.reviewCount"
                :rating="tutor.rating"
                :selectedLanguages="languages"
            />
            <v-row>
                <v-col cols="12">
                    <v-row
                        class="mb-6"
                        justify="center"
                        no-gutters
                    >
                        <v-col lg="6">
                            <h2 class="display-1 mb-4 text-center" v-text="$t('MentorPageCalendarTitle')"></h2>
                            <p class="body-1 mb-2 text-center" v-text="$t('MentorPageCalendarDescription')"></p>
                        </v-col>
                    </v-row>
                    <v-col cols="12">
                        <calendar :calendarLoading="calendarLoading" :timeslots="timeslots" @monthChange="getCalendar" :tutorId="tutor.userData.id" :editable="false"/>
                    </v-col>
                </v-col>
                <v-col lg="12" class="text-center">
                    <v-btn color="primary" dark @click="requestSessionDialog = true">{{$t('NewSessionButton')}}</v-btn>
                </v-col>
                <v-col cols="12" class="pa-lg-12 background-section">
                    <v-container>
                        <v-row
                            class="mb-6"
                            justify="center"
                            no-gutters
                        >
                            <v-col lg="6">
                                <h2 class="display-1 mb-4 text-center" v-text="$t('Resources')"></h2>
                            </v-col>
                        </v-row>
                        <v-row justify="center">
                            <v-col 
                                cols="12" 
                                md="4"
                                lg="3"
                                v-for="(resource, index) in resources"
                                :key="index"
                            >
                                <resource :resource="resource"/>
                            </v-col>
                            <v-col v-if="resources.length == 0" class="pa-0">
                                <div class="body-1 text-center" v-text="$t('MentorResourcesAvailabilityCondition')"></div>
                            </v-col>
                        </v-row>
                        <div class="text-center">
                            <v-btn 
                                rounded
                                depressed
                                class="mt-12 mb-6 mx-auto"
                                color="primary"
                                v-if="resources.length > 0"
                                :to="`/${$i18n.locale}/student/tutor/resources/${tutor.userData.id}`"
                                v-text="$t('ShowMore')"
                            ></v-btn>
                        </div>
                    </v-container>
                </v-col>
                <v-col cols="12" class="pa-12">
                    <v-container>
                        <v-row
                            class="mb-6"
                            justify="center"
                            no-gutters
                        >
                            <v-col lg="6">
                                <h2 class="mb-4 text-center" :class="{'title': $vuetify.breakpoint.smAndDown, 'display-1': $vuetify.breakpoint.mdAndUp}" v-text="$t('Reviews')"></h2>
                                <v-row no-gutters justify="center">
                                    <div class="font-weight-bold mr-2" :class="{'subtitle-1': $vuetify.breakpoint.smAndDown, 'headline': $vuetify.breakpoint.mdAndUp}" v-text="tutor.rating"></div>
                                    <v-col cols="12" class="d-flex justify-center">
                                        <v-rating
                                            class="d-inline-block"
                                            :value="tutor.rating"
                                            dense
                                            half-increments
                                            background-color="orange lighten-3"
                                            color="orange"
                                            readonly
                                            size="35"
                                        ></v-rating>
                                    </v-col>
                                    <v-col cols="12" class="d-flex justify-center">
                                        <div class="title font-weight-medium" :class="{'subtitle-1': $vuetify.breakpoint.smAndDown, 'headline': $vuetify.breakpoint.mdAndUp}">{{ tutor.reviewCount }} {{ $t('Reviews') }}</div>
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>
                        <v-row justify="center">
                            <v-col sm="12" md="10" lg="8" v-if="!loadingReviews">
                                <template v-for="(reviewItem, index) in reviewsList">
                                    <v-divider :key="index"></v-divider> 
                                    <v-row class="px-4 pt-2 pb-4" :key="reviewItem.description + Math.floor(Math.random() * 300)">
                                        <v-col cols="12" sm="6" md="5" class="d-flex align-center">
                                            <v-avatar size="85" class="mr-4" color="grey lighten-4">
                                                <v-img v-if="reviewItem.student.photo"  :src="`${baseImageUrl}${reviewItem.student.photo}`"></v-img>
                                                <v-icon v-else>
                                                    mdi-account
                                                </v-icon>
                                            </v-avatar>
                                            <div class="d-flex flex-column">
                                                <div class="title">{{ reviewItem.student.fullName }}</div>     
                                                <div v-if="reviewItem.student.country" class="font-weight-regular">({{ reviewItem.student.country.label }})</div>                                
                                            </div>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="7">
                                            <div class="pl-4">
                                                <div class="d-flex align-center">
                                                    <v-chip outlined small color="primary" class="mt-1 mr-2" v-text="reviewItem.teachingSubjectName"></v-chip>
                                                    <v-rating
                                                        class="d-inline-block"
                                                        :value="reviewItem.rating"
                                                        dense
                                                        half-increments
                                                        background-color="orange lighten-3"
                                                        color="orange"
                                                        readonly
                                                        size="20"
                                                    ></v-rating>
                                                </div>
                                                <v-row>
                                                    <v-col cols="12" md="8">
                                                        <div class="body-1 mt-2 mb-4">"{{ reviewItem.description }}"</div>
                                                    </v-col>
                                                </v-row>
                                                <div class="overline" v-text="dateFormat(reviewItem.date)"></div>                                                    
                                            </div>
                                        </v-col>
                                    </v-row>                                                                          
                                </template>
                            </v-col>
                        </v-row>
                    </v-container>   
                        <div class="text-center">
                            <v-btn 
                                rounded
                                depressed
                                class="mt-12 mb-6 mx-auto"
                                color="primary"
                                v-if="reviewsOffset != -9999"
                                @click="fetchTutorReviews"
                                v-text="$t('ShowMore')"
                            ></v-btn>
                        </div>                 
                </v-col>
            </v-row>
        </template>
        <v-dialog
            v-model="previewResourceDialog"
            width="800"
        >
            <resource-details :resource="previewResource"/>
        </v-dialog>
        <v-dialog
            v-model="requestSessionDialog"
            max-width="500px"
        >
            <v-card>
            <v-card-title class="mb-3">
                {{$t('NewSessionTitle')}}
            </v-card-title>
            <v-card-subtitle>{{$t('NewSessionDescription')}}</v-card-subtitle>
            <v-card-text>
                <v-select
                :items="tutor.teachingSubjects"
                :label="$t('NewSessionSelectSubject')"
                item-text="label"
                item-value="id"
                v-model="subjectId"
                ></v-select>
            </v-card-text>

            <v-card-text>
             <v-textarea rows="1" auto-grow :label="$t('NewSessionMessage')" v-model="message"></v-textarea>
            </v-card-text>
            <v-card-actions class="justify-center">
                <v-btn color="primary" depressed @click="submitSubjectRequest">Submit</v-btn>
                <v-btn color="primary" text @click="requestSessionDialog = false">Close</v-btn>
            </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
    import TutorHero from '@/components/tutorHero'
    import Calendar from '@/components/calendar'
    import Resource from '@/components/resourceCard'
    import ResourceDetails from '@/components/resourceCardDetails'
    import { mapGetters } from 'vuex'
    import moment from 'moment'

    export default {
        name: 'tutorDetails',
        data: () => ({
            requestSessionDialog: false,
            tutor: {},
            resources: [],
            reviewsList : [],
            reviewsOffset : 0,
            timeslots: [],
            languages: [],
            previewResource: {},
            previewResourceDialog: false,
            loadingProfile: true,
            loadingReviews: true,
            calendarLoading: true,
            subjectId: null,
            message: null,
            snackbar: {
                show: true,
                message: ''
            }
        }),
        components: {
            TutorHero,
            Calendar,
            Resource,
            ResourceDetails
        },
        computed: {
            ...mapGetters(['baseImageUrl', 'preferedLanguage']),
            tutorName() {
                return this.tutor.userData.firstName + ' ' + this.tutor.userData.lastName
            },
            profilePicture() {
                return this.baseImageUrl + this.tutor.userData.photo
            }
        },
        mounted() {
            this.fetchTutor()
            this.fetchTutorReviews()
            this.$EventBus.$on('previewResource', this.previewResourceEventHandler)
        },
        methods: {
            async submitSubjectRequest(){
                let id = this.$route.params.id
                let subjectId = this.subjectId;
                let message = this.message;
                await this.axios.post('/Tutor/RequestSession', null, {
                    params:{
                        tutorId: id,
                        subjectId: subjectId,
                        message: message
                    }
                })
                .then(response => {
                    this.requestSessionDialog = false;
                    this.snackbar.message = this.$t('NewSessionConfirmation')
                    this.$EventBus.$emit('snackbar', this.snackbar)
                })
                .catch(error => {
                    this.snackbar.message = error.message
                    this.$EventBus.$emit('snackbar', this.snackbar)
                })
            },
            async fetchTutor() {
                let id = this.$route.params.id
                await this.axios.get('/Tutor/GetTutor', {
                    params: {
                        tutorId: id
                    }
                })
                .then(response => {
                    this.tutor = response.data.tutor
                    this.resources = response.data.resources
                    this.timeslots = response.data.timeslots
                    this.languages = response.data.languages
                })
                .catch(error => {
                    this.snackbar.message = error.message
                    this.$EventBus.$emit('snackbar', this.snackbar)
                })
                this.loadingProfile = false
                this.calendarLoading = false
            },
            async fetchTutorReviews(){
                let id = this.$route.params.id
                let offset = this.reviewsOffset;
                let amount = 2;
                let languageId = this.preferedLanguage; 

                await this.axios.get('/Review/GetReviewsByTutorId', {
                    params: {
                        tutorId: id,
                        languageId : languageId,
                        offset : offset,
                        amount : amount
                    }
                })
                .then(response => {
                    response.data.reviewsList.forEach(element => this.reviewsList.push(element));
                    this.reviewsOffset = response.data.offset
                })
                .catch(error => {
                    this.snackbar.message = error.message
                    this.$EventBus.$emit('snackbar', this.snackbar)
                })

                this.loadingReviews = false;
            },
            previewResourceEventHandler(resource) {
                this.previewResourceDialog = true
                this.previewResource = resource
            },
            dateFormat(date) {
                return moment(date).format("MMMM DD, YYYY")
            },
            async getCalendar(date) {
                this.calendarLoading = true
                let id = this.$route.params.id
                await this.axios.get('/Calendar/GetCalendar', {
                    params: {
                        tutorId: id,
                        date: date
                    }
                })
                .then(response => {
                    this.timeslots = response.data.timeslots
                    this.calendarLoading = false
                })
                .catch(error => {
                    this.snackbar.message = error.message
                    this.$EventBus.$emit('snackbar', this.snackbar)
                    this.calendarLoading = false
                })
            }
        }
    }
</script>

<style lang="scss" scoped>
.background-section{
    background-color: #f8f8f8;
}
</style>